import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useI18n } from '../hooks/useI18n';
import {
  faChartLine,
  faBox,
  faTag,
  faStar,
  faTrash,
  faBell,
  faCog,
  faSignOutAlt,
  faHistory,
  faUsersCog,
  faBuilding,
  faCreditCard,
  faPlus,
  faBrain,
  faCalculator,
  faLayerGroup,
  faCalendarAlt,
  faRobot,
  faFingerprint,
  faShieldAlt,
  faExchangeAlt,
  faBars,
  faArrowLeft,
  faSearch,
  faPaperPlane,
  faTimes as faXmark,
  faQuestionCircle,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { RootState } from '../store';
import '../styles/DashboardLayout.css';
import { notificationsAPI, trialAPI } from '../services/api';
import useWebSocket from '../hooks/useWebSocket';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { NotificationCenter, NotificationToastContainer } from './NotificationToast';
import FeedbackModal from './FeedbackModal';
import ItemModal from './ItemModal';
import LimitReachedModal from './LimitReachedModal';
import { trackFeature } from '../services/telemetryService';
import { compressImages } from '../utils/imageCompression';
import { api } from '../services/api';
import { secureLogout } from '../utils/secureLogout';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  relatedItemId?: string;
  relatedUserId?: string;
  actionUrl?: string;
  createdAt: string;
  isRead: boolean;
}

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth <= 767);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { t } = useI18n();

  // Auto-logout on inactivity (30 min)
  useInactivityLogout();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Global plan limit reached modal state
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitModalType, setLimitModalType] = useState<'items' | 'storage' | 'images' | 'workspaces' | 'members' | 'categories' | 'ai_messages' | 'generic'>('generic');
  const [limitModalCurrent, setLimitModalCurrent] = useState<number | undefined>(undefined);
  const [limitModalMax, setLimitModalMax] = useState<number | undefined>(undefined);
  const [hasActiveModal, setHasActiveModal] = useState(false);
  const {
    notifications: liveNotifications,
    markAsRead: markAsReadSocket,
    clearNotification,
    clearAllNotifications,
  } = useWebSocket();

  const handleLogout = async () => {
    trackFeature('logout');
    await secureLogout(dispatch as any, navigate);
  };

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    // Keep viewport breakpoint state in sync and close overlay sidebar off-mobile.
    const handleResize = () => {
      const isMobile = window.innerWidth <= 767;
      setIsMobileViewport(isMobile);
      if (!isMobile) {
        setMobileSidebarOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setMobileSidebarOpen(false);
    }
  }, [location.pathname]);

  // Listen for tutorial sidebar open/close events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.open) {
        setMobileSidebarOpen(true);
      } else {
        setMobileSidebarOpen(false);
      }
    };
    window.addEventListener('elvoaq-tutorial-sidebar', handler);
    return () => window.removeEventListener('elvoaq-tutorial-sidebar', handler);
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchCategoriesAndCurrencies();
    // Silently check & auto-downgrade expired trials on mount
    trialAPI.checkExpiry().catch(() => {});
  }, []);

  // Global listener for PLAN_LIMIT_REACHED events dispatched by the API interceptor
  useEffect(() => {
    const handlePlanLimit = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const typeMap: Record<string, any> = {
        items: 'items', storage: 'storage', images: 'images',
        workspaces: 'workspaces', members: 'members',
        categories: 'categories', ai_messages: 'ai_messages',
      };
      setLimitModalType(typeMap[detail?.limitType] || 'generic');
      setLimitModalCurrent(detail?.current);
      setLimitModalMax(detail?.max);
      setLimitModalOpen(true);
    };
    window.addEventListener('plan-limit-reached', handlePlanLimit);
    return () => window.removeEventListener('plan-limit-reached', handlePlanLimit);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownOpen && !(event.target as Element).closest('.user-section')) {
        setUserDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);

  useEffect(() => {
    const overlaySelectors = [
      '.modal-overlay',
      '.advanced-filters-overlay',
      '.feedback-modal-overlay',
      '.limit-modal-overlay',
      '.ecol-overlay',
      '.ecal-modal-overlay',
      '.plm-overlay',
      '.keyboard-shortcuts-overlay',
      '.item-detail-overlay',
    ];

    const checkActiveOverlay = () => {
      const active = overlaySelectors.some((selector) => !!document.querySelector(selector));
      setHasActiveModal(active);
      document.body.classList.toggle('elvoaq-modal-active', active);
    };

    checkActiveOverlay();
    const observer = new MutationObserver(checkActiveOverlay);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      observer.disconnect();
      document.body.classList.remove('elvoaq-modal-active');
    };
  }, [feedbackModalOpen, itemModalOpen, limitModalOpen]);

  const fetchCategoriesAndCurrencies = async () => {
    try {
      const [categoriesRes, currenciesRes] = await Promise.all([
        api.get('/categories'),
        api.get('/metadata/currencies'),
      ]);
      setCategories(categoriesRes.data);
      setCurrencies(currenciesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const handleSaveItem = async (itemData: any, files: File[] = []) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', itemData.name);
      formDataToSend.append('description', itemData.description || '');
      if (itemData.categoryId && itemData.categoryId !== '') {
        formDataToSend.append('categoryId', itemData.categoryId);
      }
      formDataToSend.append('condition', itemData.condition);
      formDataToSend.append('location', itemData.location || '');
      formDataToSend.append('purchaseCurrency', itemData.purchaseCurrency || 'USD');
      if (itemData.purchasePrice) {
        formDataToSend.append('purchasePrice', itemData.purchasePrice.toString());
      }
      if (itemData.tags && itemData.tags.length > 0) {
        formDataToSend.append('tags', JSON.stringify(itemData.tags));
      }

      const response = await api.post('/items', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (files.length > 0) {
        try {
          const compressed = await compressImages(files);
          const uploadData = new FormData();
          compressed.forEach((uploadFile) => uploadData.append('images', uploadFile));
          await api.post(`/items/${response.data.id}/images`, uploadData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (imgError) {
          console.error('Image upload failed:', imgError);
        }
      }
      
      setItemModalOpen(false);
      // Refresh the page data or trigger a refetch
      window.location.reload();
    } catch (error) {
      console.error('Failed to save item:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (liveNotifications.length === 0) return;
    setNotifications((prev) => {
      const merged = [...prev];
      for (const live of liveNotifications) {
        if (!merged.find((n) => n.id === live.id)) {
          merged.unshift(live as Notification);
        }
      }
      setUnreadCount(merged.filter((n) => !n.isRead).length);
      return merged;
    });
  }, [liveNotifications]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsAPI.markAsRead(id);
      markAsReadSocket([id]);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAsUnread = async (id: string) => {
    try {
      await notificationsAPI.markAsUnread(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: false } : n)));
      setUnreadCount((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to mark notification as unread:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      markAsReadSocket([]);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleClear = async (id: string) => {
    try {
      const target = notifications.find((n) => n.id === id);
      await notificationsAPI.delete(id);
      clearNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (target && !target.isRead) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationsAPI.deleteAll();
      clearAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const navItems = [
    { path: '/', label: t('nav.dashboard'), icon: faChartLine },
    { path: '/library', label: t('nav.library'), icon: faBox },
    { path: '/categories', label: t('nav.categories'), icon: faTag },
    { path: '/favorites', label: t('nav.favorites'), icon: faStar },
    { path: '/accounting', label: t('nav.accounting'), icon: faCalculator },
    { path: '/reminders', label: t('nav.smartHub'), icon: faBrain },
    { path: '/ai-assistant', label: t('nav.aiAssistant'), icon: faRobot },
    { path: '/duplicate-finder', label: t('nav.duplicateFinder'), icon: faFingerprint },
    { path: '/hidden-folders', label: t('nav.hiddenFolders'), icon: faShieldAlt },
    { path: '/data-transfer', label: t('nav.importExport'), icon: faExchangeAlt },
    { path: '/trash', label: t('nav.trash'), icon: faTrash },
    { path: '/notifications', label: t('nav.notifications'), icon: faBell },
  ];

  const otherNavItems = [
    { path: '/workspaces', label: t('nav.workspaces'), icon: faBuilding },
    { path: '/users', label: t('nav.usersRoles'), icon: faUsersCog },
    { path: '/collections', label: t('nav.collections'), icon: faLayerGroup },
    { path: '/calendar', label: t('nav.calendar'), icon: faCalendarAlt },
    { path: '/audit', label: t('nav.auditTrail'), icon: faHistory },
    { path: '/settings', label: t('nav.settings'), icon: faCog, keywords: ['language', 'languages', 'profile', 'preferences', 'account'] },
  ];

  const billingNavItems = [
    { path: '/subscription', label: t('nav.subscription'), icon: faCreditCard },
  ];

  const sidebarKeywordDictionary: Record<string, string[]> = {
    '/settings': [
      'settings', 'setting', 'preferences', 'preference', 'profile', 'account', 'accounts',
      'language', 'languages', 'lang', 'einstellungen', 'sprache',
      'nastaveni', 'nastaveni', 'nastavenia', 'jazyk', 'jazyk',
    ],
    '/subscription': ['subscription', 'billing', 'plan', 'upgrade', 'abonement', 'predplatne'],
    '/notifications': ['notifications', 'notification', 'alerts', 'upozornenia', 'upozorneni', 'benachrichtigungen'],
    '/workspaces': ['workspace', 'workspaces', 'team', 'teams', 'arbeitsbereich', 'pracovni prostor'],
    '/audit': ['audit', 'audit log', 'history', 'protokoll', 'historie'],
  };

  // Super Admin is NOT part of customer UI.
  // Access platform admin console at /platform-admin (separate route with own layout).

  const handleSidebarScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    
    // Prevent parent scroll when at scroll boundaries
    if ((e.deltaY < 0 && scrollTop === 0) || 
        (e.deltaY > 0 && scrollTop + clientHeight >= scrollHeight)) {
      e.preventDefault();
    }
  };

  const closeSidebarOnMobile = () => {
    if (window.innerWidth <= 768) {
      setMobileSidebarOpen(false);
    }
  };

  const handleSidebarInteraction = (event: React.MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (!target) return;

    const shouldClose = target.closest('.nav-item, .dropdown-item, .category-item, .nav-feedback-btn');
    if (shouldClose) {
      closeSidebarOnMobile();
      setUserDropdownOpen(false);
    }
  };

  const handleHelpAction = () => {
    window.dispatchEvent(new CustomEvent('elvoaq-start-tutorial'));
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate('/');
  };

  const toggleSidebar = () => {
    if (window.innerWidth <= 768) {
      setMobileSidebarOpen((prev) => !prev);
      return;
    }
    setSidebarCollapsed((prev) => !prev);
  };

  // All nav items combined for sidebar search (with multilingual keyword fallback)
  const allSidebarItems = useMemo(() => {
    const withKeywords = (item: any, section: string) => ({
      ...item,
      section,
      keywords: [
        ...((item.keywords as string[] | undefined) || []),
        ...(sidebarKeywordDictionary[item.path] || []),
      ],
    });

    return [
      ...navItems.map(item => withKeywords(item, t('nav.sectionMenu'))),
      ...otherNavItems.map(item => withKeywords(item, t('nav.sectionOther'))),
      ...billingNavItems.map(item => withKeywords(item, t('nav.sectionBilling'))),
    ];
  }, [navItems, otherNavItems, billingNavItems, t]);

  const filteredSidebarItems = useMemo(() => {
    const q = sidebarSearch.trim().toLowerCase();
    if (!q) return [];
    return allSidebarItems.filter(item => {
      const haystack = [item.label, item.section, ...(item.keywords || [])]
        .filter(Boolean)
        .map((v) => v.toLowerCase())
        .join(' ');
      return haystack.includes(q);
    });
  }, [allSidebarItems, sidebarSearch]);

  const currentPageTitle = useMemo(() => {
    const routeWithHash = location.pathname + (location.hash || '');
    const exact = allSidebarItems.find((item) => item.path === routeWithHash || item.path === location.pathname);
    if (exact?.label) return exact.label;

    if (location.pathname === '/') return t('nav.dashboard');

    const lastSegment = location.pathname.split('/').filter(Boolean).pop() || '';
    if (!lastSegment) return t('nav.dashboard');

    return lastSegment
      .split(/[-_]/g)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }, [allSidebarItems, location.pathname, location.hash, t]);

  return (
    <div className={`dashboard-layout ${hasActiveModal ? 'modal-active' : ''}`}>
      {/* Mobile overlay */}
      {mobileSidebarOpen && (
        <div
          className="sidebar-mobile-overlay"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarCollapsed ? 'closed' : 'open'} ${mobileSidebarOpen ? 'mobile-open' : ''} ${userDropdownOpen ? 'user-dropdown-open' : ''}`}
        onClickCapture={handleSidebarInteraction}
      >
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <span className="brand-primary">ELVOAQ</span>
            <span className="brand-secondary">VAULT</span>
          </div>
          <button
            className="sidebar-mobile-close"
            onClick={() => setMobileSidebarOpen(false)}
            title="Close menu"
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <button
            className="toggle-btn"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>

        {/* Sidebar search */}
        <div className="sidebar-search-wrap">
          <FontAwesomeIcon icon={faSearch} className="sidebar-search-icon" />
          <input
            type="text"
            className="sidebar-search-input"
            placeholder={t('nav.searchPlaceholder') || 'Search…'}
            value={sidebarSearch}
            onChange={e => setSidebarSearch(e.target.value)}
          />
          {sidebarSearch && (
            <button className="sidebar-search-clear" onClick={() => setSidebarSearch('')} aria-label="Clear search">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>

        <div className="sidebar-nav-scroll" onWheel={handleSidebarScroll}>
          <nav className="sidebar-nav">
            {/* --- Filtered search results --- */}
            {sidebarSearch.trim() ? (
              <div className="nav-section">
                {filteredSidebarItems.length > 0 ? (
                  filteredSidebarItems.map(item => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                      title={item.label}
                      onClick={() => { setSidebarSearch(''); closeSidebarOnMobile(); }}
                    >
                      <span className="nav-icon"><FontAwesomeIcon icon={item.icon} /></span>
                      <span className="nav-label">{item.label}</span>
                      <span className="nav-search-section-tag">{item.section}</span>
                    </Link>
                  ))
                ) : (
                  <div className="nav-search-no-results">
                    <FontAwesomeIcon icon={faSearch} />
                    <span>{t('nav.searchNoResults') || 'No results'}</span>
                  </div>
                )}
              </div>
            ) : (
              <>
            <div className="nav-section">
              <div className="nav-section-title">{t('nav.sectionMenu')}</div>
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                  title={item.label}
                  onClick={closeSidebarOnMobile}
                >
                  <span className="nav-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              ))}
            </div>

            <div className="nav-section">
              <div className="nav-section-title">{t('nav.sectionOther')}</div>
              {otherNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                  title={item.label}
                  onClick={closeSidebarOnMobile}
                >
                  <span className="nav-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              ))}
            </div>

            <div className="nav-section">
              <div className="nav-section-title">{t('nav.sectionBilling')}</div>
              {billingNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${location.pathname === item.path || location.pathname + location.hash === item.path ? 'active' : ''}`}
                  title={item.label}
                  onClick={closeSidebarOnMobile}
                >
                  <span className="nav-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              ))}
            </div>
            <div className="nav-section nav-section-divider">
              <button
                onClick={() => { setFeedbackModalOpen(true); closeSidebarOnMobile(); }}
                className="nav-item nav-feedback-btn"
                title={t('nav.feedback')}
              >
                <span className="nav-icon">
                  <FontAwesomeIcon icon={faPaperPlane} />
                </span>
                <span className="nav-label">{t('nav.feedback')}</span>
              </button>
            </div>
            </>
            )}

          </nav>
        </div>

        <div className="sidebar-footer">
          <div className="user-section">
            <button
              className="user-info-btn"
              onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              title={t('dropdown.openProfile')}
            >
              <div className="avatar-wrapper">
                <div className="user-avatar-small">
                  {user?.profilePicture ? (
                    <img src={user.profilePicture} alt="Profile" className="avatar-image" />
                  ) : (
                    <span className="avatar-initials">
                      {user?.firstName?.[0]?.toUpperCase() || ''}{user?.lastName?.[0]?.toUpperCase() || ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="user-details-compact">
                <span className="user-name-compact">
                  {`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email?.split('@')?.[0] || t('dropdown.userFallback')}
                </span>
                <span className="user-cta-compact">{t('dropdown.openProfile')}</span>
              </div>
              <FontAwesomeIcon icon={faChevronRight} className="dropdown-icon" />
            </button>
            {userDropdownOpen && (
              <div className="user-dropdown">
                <Link to="/settings" className="dropdown-item" onClick={() => setUserDropdownOpen(false)}>
                  <FontAwesomeIcon icon={faCog} />
                  <span>{t('dropdown.settings')}</span>
                </Link>
                <Link to="/subscription" className="dropdown-item" onClick={() => setUserDropdownOpen(false)}>
                  <FontAwesomeIcon icon={faCreditCard} />
                  <span>{t('dropdown.subscription')}</span>
                </Link>
                <button className="dropdown-item logout-item" onClick={handleLogout}>
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  <span>{t('dropdown.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-closed' : 'sidebar-open'}`}>
        <header className={`content-header ${hasActiveModal ? 'header-modal-dim' : ''}`}>
          <div className="header-left elvoaq-header-branding">
            {isMobileViewport && (
              <button
                className="mobile-hamburger header-icon-btn"
                onClick={toggleSidebar}
                title={mobileSidebarOpen ? 'Close menu' : 'Open menu'}
                aria-label={mobileSidebarOpen ? 'Close menu' : 'Open menu'}
              >
                <FontAwesomeIcon icon={mobileSidebarOpen ? faXmark : faBars} />
              </button>
            )}
            <button
              className="back-button header-icon-btn"
              onClick={handleBack}
              title={t('common.back') || 'Back'}
              aria-label={t('common.back') || 'Go back'}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div className="header-title-group" aria-live="polite">
              <span className="page-title">{currentPageTitle}</span>
            </div>
          </div>
          <div className="header-right header-actions elvoaq-header-actions">
            <button
              className="add-item-button elvoaq-create-btn"
              onClick={() => setItemModalOpen(true)}
              title="Create"
              aria-label="Create new entry"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Create</span>
            </button>
            <div className="header-divider action-divider" />
            <div className="notification-wrapper">
              <NotificationCenter
                notifications={notifications}
                unreadCount={unreadCount}
                onMarkAsRead={handleMarkAsRead}
                onMarkAsUnread={handleMarkAsUnread}
                onMarkAllAsRead={handleMarkAllAsRead}
                onClear={handleClear}
                onClearAll={handleClearAll}
                onNavigate={(url) => navigate(url)}
              />
            </div>
            <button
              className="header-action-btn elvoaq-help-btn"
              onClick={handleHelpAction}
              title="Help"
              aria-label="Help and tutorial"
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
              <span>Help</span>
            </button>
            <button
              className="header-action-btn elvoaq-feedback-btn"
              onClick={() => setFeedbackModalOpen(true)}
              title={t('nav.feedback')}
              aria-label={t('nav.feedback')}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              <span>{t('nav.feedback')}</span>
            </button>
          </div>
        </header>
        <div className="content-body">{children}</div>
        <NotificationToastContainer
          notifications={liveNotifications as Notification[]}
          onDismiss={clearNotification}
        />
      </main>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        onSuccess={() => {
          setFeedbackModalOpen(false);
          // Optionally navigate to feedback page or show success message
        }}
      />
      <ItemModal
        isOpen={itemModalOpen}
        onClose={() => setItemModalOpen(false)}
        onSave={handleSaveItem}
        categories={categories}
        currencies={currencies}
      />
      {/* Global Plan Limit Reached Modal */}
      <LimitReachedModal
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        limitType={limitModalType as any}
        currentUsage={limitModalCurrent}
        maxAllowed={limitModalMax}
        planName={user?.planType || 'Free'}
      />
    </div>
  );
};

export default DashboardLayout;
